import { useEffect } from "react";
import { MetabaseEntry } from "."
import { useHookFormContext } from "../../../../utils/hooks/useHookFormContext";
import { Grid, Button, IconButton, Typography, Paper, Box, Divider } from "@mui/material";
import { HookTextField } from "../../../../atoms/form-fields/SLFieldTextField";
import { HookSelect } from "../../../../atoms/form-fields/SLFieldSelect";
import { commonInputStyles } from "../../../../common/input-styles";
import FormCancelSubmitBtns from "../../../../atoms/formCancelSubmitBtns";
import { useFieldArray } from "react-hook-form";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const Fields = ({ handleClose }: any) => {
    const { registerState, getValues, control } = useHookFormContext();

    const { fields, append, remove, move } = useFieldArray({
        control,
        name: "metabaseEntries"
    });

    const addNewEntry = () => {
        append({ name: "", id: "", params: "", type: "dashboard" });
    };

    const removeEntry = (index: number) => {
        remove(index);
    };

    const handleMove = (from: number, to: number) => {
        if (to >= 0 && to < fields.length) {
            move(from, to);
        }
    };

    return (
        <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    Metabase Dashboards
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <Grid container direction="column" spacing={2}>
                    {fields.map((field, index) => (
                        <Grid item key={field.id}>
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 3,
                                    borderRadius: 2,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        elevation: 4,
                                        boxShadow: 3
                                    }
                                }}
                            >
                                <Grid container spacing={3} alignItems="flex-start">
                                    {/* Reorder Controls */}
                                    <Grid item xs={12} sm="auto">
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                                bgcolor: 'background.default',
                                                borderRadius: 1,
                                                p: 0.5
                                            }}
                                        >
                                            <IconButton
                                                onClick={() => handleMove(index, index - 1)}
                                                disabled={index === 0}
                                                size="small"
                                                color="primary"
                                                sx={{
                                                    transition: 'all 0.2s'
                                                }}
                                            >
                                                <ArrowUpwardIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleMove(index, index + 1)}
                                                disabled={index === fields.length - 1}
                                                size="small"
                                                color="primary"
                                                sx={{
                                                    transition: 'all 0.2s'
                                                }}
                                            >
                                                <ArrowDownwardIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm container spacing={2}>
                                        <Grid item xs={12} md={3}>
                                            <HookTextField
                                                {...registerState(`metabaseEntries.${index}.name`)}
                                                textFieldProps={{
                                                    ...commonInputStyles,
                                                    label: "Name",
                                                    required: true,
                                                    type: "text",
                                                    size: "small",
                                                    fullWidth: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <HookTextField
                                                {...registerState(`metabaseEntries.${index}.id`)}
                                                textFieldProps={{
                                                    ...commonInputStyles,
                                                    label: "ID",
                                                    required: true,
                                                    type: "text",
                                                    size: "small",
                                                    fullWidth: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3.5}>
                                            <HookTextField
                                                {...registerState(`metabaseEntries.${index}.params`)}
                                                textFieldProps={{
                                                    ...commonInputStyles,
                                                    label: "Params (JSON)",
                                                    type: "text",
                                                    size: "small",
                                                    multiline: true,
                                                    rows: 3,
                                                    placeholder: '{"key": "value"}',
                                                    fullWidth: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2.5}>
                                            <HookSelect
                                                {...registerState(`metabaseEntries.${index}.type`)}
                                                label="Type"
                                                items={[
                                                    { label: "Question", value: "question" },
                                                    { label: "Dashboard", value: "dashboard" }
                                                ]}
                                                selectProps={{
                                                    ...commonInputStyles,
                                                    size: "small",
                                                    fullWidth: true,
                                                    sx: {
                                                        fontSize: '10px',
                                                        '& .MuiInputBase-input': {
                                                            fontSize: '10px',
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            fontSize: '10px',
                                                        }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12} sm="auto">
                                        <IconButton
                                            onClick={() => removeEntry(index)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Grid>

            <Grid item xs={12} sx={{ mt: 3, mb: 4 }}>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addNewEntry}
                    sx={{
                        borderStyle: 'dashed',
                        borderWidth: 2,
                        py: 1.5,
                        px: 3,
                        '&:hover': {
                            borderStyle: 'dashed',
                            borderWidth: 2,
                            bgcolor: 'action.hover'
                        }
                    }}
                >
                    Add New Entry
                </Button>
            </Grid>

            <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
            </Grid>
            <Grid item xs={12}>
                <FormCancelSubmitBtns handleClose={handleClose} />
            </Grid>
        </Grid>

    )
}

export default Fields
